import {
  Box,
  Flex,
  FormControl,
  HStack,
  Icon,
  IconButton,
  InputGroup,
  PinInput,
  PinInputField,
  Spinner,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  Button as ChakraButton,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useContext, useEffect, useState } from 'react';
import { Stack } from '@chakra-ui/layout';
import { useHistory } from 'react-router';
import { FiDownload, FiSearch } from 'react-icons/fi';
import download from 'downloadjs';
import {
  AiOutlineFileSearch,
  FaEye,
  HiOutlineDocumentCheck,
} from 'react-icons/all';
import {
  Button,
  Heading,
  Input,
  Pagination,
  Select,
  StatusBadge,
  Table,
} from '~/components';
import { useRequestContractTerminationList } from '~/hooks/useTerminationRequests';
import { useQueryParams, useRoleCan } from '~/hooks';
import { TerminationRequestsList } from '~/types/terminationRequests';
import { PAGE_SIZE } from '~/constants';
import { updateUrlParams } from '~/utils/updateUrlParams';
import { queryClient } from '~/utils/react-query';
import { FilterContext } from '~/contexts/FilterContext/FilterContext';
import {
  downloadListXls,
  downloadTerminationPdf,
  generateTerminationToken,
  verifyTerminationToken,
} from '~/services/TerminationRequests';
import { ConfirmationDialog } from '~/components/ConfirmationDialog';
import { AuthContext } from '~/contexts';

export const RequestContractTerminationList: React.VFC = () => {
  const queryParams = useQueryParams();
  const { groups, headOffices, branches } = useContext(FilterContext);
  const toast = useToast();
  const history = useHistory();
  const isClient = useRoleCan({ roles: ['cliente'] });
  const { user } = useContext(AuthContext);
  const isAdmin = useRoleCan({ roles: ['administrador'] });
  const [isFetchingDownloadTermination, setIsFetchingDownloadTermination] =
    useState(false);
  const [isFetchingVerifyToken, setIsFetchingVerifyToken] = useState(false);
  const [selectedTerminationId, setSelectedTerminationId] = useState(0);
  const [codigo, setCodigo] = useState<string>('');
  const [listaContratos, setListaContratos] = useState<
    TerminationRequestsList[]
  >([]);
  const [isLoadingDownload, setIsLoadingDownload] = useState(false);
  const [searchTmp, setSearchTmp] = useState(() => {
    return queryParams.get('busca') ?? '';
  });
  const {
    isOpen: isOpenModalToken,
    onOpen: onOpenModalToken,
    onClose: onCloseModalToken,
  } = useDisclosure();
  const [parametros, setParametros] = useState(() => {
    return {
      busca: queryParams.get('busca') ?? '',
      situacao: queryParams.get('situacao') ?? '',
      solicitante: queryParams.get('solicitante') ?? '',
      pagina: queryParams.get('pagina') ?? '1',
      paginacao: queryParams.get('paginacao') ?? PAGE_SIZE.toString(),
    };
  });
  const { data, isLoading, isFetching, error, refetch } =
    useRequestContractTerminationList(
      Number(parametros.pagina) ?? 1,
      parametros.busca,
      parametros.situacao,
      parametros.solicitante,
      Number(parametros.paginacao),
      {
        onSuccess: d => {
          setListaContratos(d.results);
        },
      },
      isAdmin,
    );

  useEffect(() => {
    queryClient.invalidateQueries('resquest-contract-termination-list');
  }, []);

  useEffect(() => {
    setParametros({
      ...parametros,
      pagina: '1',
    });
    updateUrlParams(
      {
        ...parametros,
        pagina: '1',
      },
      history,
    );
    queryClient.invalidateQueries('resquest-contract-termination-list');
  }, [groups, headOffices, branches]);

  const handleSolicitanteChange = (e: any) => {
    setParametros({
      ...parametros,
      solicitante: e.target.value,
      pagina: '1',
    });
    updateUrlParams(
      {
        ...parametros,
        solicitante: e.target.value,
        pagina: '1',
      },
      history,
    );
    queryClient.invalidateQueries('resquest-contract-termination-list');
  };

  function handleContractStatusChange(e: any) {
    setParametros({
      ...parametros,
      situacao: e.target.value,
      pagina: '1',
    });
    updateUrlParams(
      {
        ...parametros,
        situacao: e.target.value,
        pagina: '1',
      },
      history,
    );
    queryClient.invalidateQueries('resquest-contract-termination-list');
  }

  const handlePageSizeChange = (e: any) => {
    setParametros({
      ...parametros,
      paginacao: e.target.value,
      pagina: '1',
    });
    updateUrlParams(
      {
        ...parametros,
        paginacao: e.target.value,
        pagina: '1',
      },
      history,
    );
    queryClient.invalidateQueries('resquest-contract-termination-list');
  };

  const handleSearchChange = (e: any) => {
    setSearchTmp(e);
    setParametros({
      ...parametros,
      busca: e,
      pagina: '1',
    });
    updateUrlParams(
      {
        ...parametros,
        busca: e,
        pagina: '1',
      },
      history,
    );
    queryClient.invalidateQueries('resquest-contract-termination-list');
  };
  const goToVisualizationAnalysis = (id: number) => {
    history.push(
      `/portal/request-contract-termination?visualization-analysis=true&terminationId=${id}`,
    );
  };
  const goToVisualization = (id: number) => {
    history.push(
      `/portal/request-contract-termination?visualization=true&terminationId=${id}`,
    );
  };
  const goToAnalysis = (id: number) => {
    history.push(
      `/portal/request-contract-termination?analysis=true&terminationId=${id}`,
    );
  };
  const handleCodigoChange = (value: any) => {
    setCodigo(value);
  };
  const generateToken = (id: number, regenerate?: boolean): void => {
    setIsFetchingVerifyToken(true);
    setSelectedTerminationId(id);
    generateTerminationToken(id)
      .then(() => {
        onOpenModalToken();
        regenerate &&
          toast({
            title: 'Sucesso',
            description: 'Token reenviado com sucesso',
            status: 'success',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
      })
      .catch(err => {
        toast({
          title: 'Erro',
          description: err.response.data.detail,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => {
        setIsFetchingVerifyToken(false);
      });
  };
  const regenerateToken = () => {
    generateToken(selectedTerminationId, true);
  };
  const verifyToken = () => {
    setIsFetchingVerifyToken(true);
    verifyTerminationToken(selectedTerminationId, codigo)
      .then(() => {
        onCloseModalToken();
        queryClient.invalidateQueries('resquest-contract-termination-list');
        toast({
          title: 'Sucesso',
          description: 'Token validado com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .catch(err => {
        toast({
          title: 'Erro',
          description: err.response.data,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => {
        setIsFetchingVerifyToken(false);
      });
  };
  const getUserEmailFormatted = () => {
    const arr = user.email.split('@');
    return arr[0].substr(0, 3).concat('***@').concat(arr[1]);
  };

  const downloadTermination = (id: number) => {
    setIsFetchingDownloadTermination(true);
    downloadTerminationPdf(id)
      .then(r => {
        if (r?.data?.size > 0) {
          download(
            new Blob([r.data], { type: r.data.type }),
            r.headers['content-disposition'].match(/filename="(.+)"/)[1],
          );
        } else {
          toast({
            title: r?.data?.message,
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(() => {
        toast({
          title: 'Termo de rescisão não encontrado',
          description: 'Entre em contato para mais informações',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => {
        setIsFetchingDownloadTermination(false);
      });
  };
  const handleDownload = async () => {
    setIsLoadingDownload(true);
    downloadListXls(
      parametros.busca,
      parametros.situacao,
      parametros.solicitante,
      { groups, headOffices, branches },
    )
      .then(r => {
        if (r.data.size > 0) {
          download(
            new Blob([r.data], { type: r.data.type }),
            r.headers['content-disposition'].match(/filename="(.+)"/)[1],
          );
        } else {
          toast({
            title: r?.data?.error,
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(async err => {
        const error2 = JSON.parse(await err.response.data.text());
        toast({
          title: `Erro`,
          description: error2.detail,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => setIsLoadingDownload(false));
  };

  return (
    <Box px="8" py="4">
      <Heading>Solicitações de Rescisão</Heading>
      <Flex justify="space-between">
        <HStack py={3} gridGap={3} w="50vw" alignItems="flex-end">
          <Input
            name="search"
            type="text"
            icon={FiSearch}
            value={searchTmp}
            onChange={e => {
              handleSearchChange(e.target.value);
            }}
            placeholder="Procure solicitação por nome da empresa, CNPJ ou contrato"
            w={490}
            fontSize="15px"
          />
          <Select
            name="faturamento"
            label="Solicitante"
            onChange={e => handleSolicitanteChange(e)}
            value={parametros.solicitante}
            w={150}
          >
            <option value="">Todos</option>
            <option value="1">Cliente</option>
            <option value="0">Positivo</option>
          </Select>
          <Select
            name="status"
            label="Situação"
            onChange={e => handleContractStatusChange(e)}
            value={parametros.situacao}
            w={370}
          >
            <option value="">Todos</option>
            {isClient && <option value="0">Solicitada</option>}
            {isAdmin && <option value="0">Pendente de análise</option>}
            <option value="1">Termo de rescisão enviado ao cliente</option>
            <option value="2">
              Aguardando assinatura do termo de rescisão
            </option>
            <option value="3">Termo de rescisão assinado</option>
            <option value="4">Cancelada</option>
          </Select>
        </HStack>
        {!isClient && (
          <Button
            disabled={isLoadingDownload || isLoading}
            onClick={handleDownload}
            color="white"
            background="gray.900"
            _hover={{
              bgColor: 'black',
            }}
            style={{ marginTop: '40px' }}
          >
            Exportar
            {isLoadingDownload && <Spinner style={{ marginLeft: '10px' }} />}
          </Button>
        )}
      </Flex>
      <Stack mt={3}>
        <TableContainer>
          <Table>
            <Thead>
              <Tr>
                <Th>ID</Th>
                <Th>EMPRESA/GRUPO</Th>
                <Th>CONTRATOS</Th>
                <Th textAlign="center">SOLICITANTE</Th>
                <Th textAlign="center">STATUS DA RESCISÃO</Th>
                <Th textAlign="center">DATA DE SOLICITAÇÃO </Th>
                <Th>AÇÃO</Th>
                <Th display={{ md: 'none', sm: 'none' }} />
              </Tr>
            </Thead>
            <Tbody>
              {listaContratos.length && !isLoading ? (
                listaContratos.map(contract => (
                  <Tr key={`${contract.id}`}>
                    <Td>{contract.id}</Td>
                    <Td>
                      <Text>{contract.empresa?.cnpjFormatado || '-'}</Text>
                      <Text>{contract.empresa?.nm_razaosocial || '-'}</Text>
                      <Text fontSize="11px">
                        {contract.grupo_economico || '-'}
                      </Text>
                    </Td>
                    <Td>{contract.nu_contratos}</Td>
                    <Td textAlign="center">
                      <StatusBadge variant={contract.solicitanteColor}>
                        {contract.solicitanteStr}
                      </StatusBadge>
                    </Td>
                    <Td textAlign="center">
                      <StatusBadge
                        variant={contract.statusColor}
                        width="fit-content"
                      >
                        {contract.statusStr}
                      </StatusBadge>
                    </Td>
                    <Td textAlign="center">
                      <Text>{contract.dt_solicitacao}</Text>
                    </Td>
                    <Td>
                      {contract.status === 0 && isClient && (
                        <Tooltip label="Visualizar solicitação">
                          <IconButton
                            colorScheme="gray"
                            background="transparent"
                            _hover={{
                              background: 'transparent',
                            }}
                            aria-label="Visualizar solicitação"
                            icon={<Icon as={FaEye} />}
                            onClick={() => goToVisualization(contract.id)}
                          />
                        </Tooltip>
                      )}
                      {contract.status === 0 &&
                        isAdmin &&
                        contract.solicitante === 1 && (
                          <Tooltip label="Analisar solicitação">
                            <IconButton
                              colorScheme="gray"
                              background="transparent"
                              _hover={{
                                background: 'transparent',
                              }}
                              aria-label="Analisar solicitação"
                              icon={<Icon as={AiOutlineFileSearch} />}
                              onClick={() => goToAnalysis(contract.id)}
                            />
                          </Tooltip>
                        )}
                      {contract.status === 0 &&
                        isAdmin &&
                        contract.solicitante === 0 && (
                          <Tooltip label="Visualizar solicitação">
                            <IconButton
                              colorScheme="gray"
                              background="transparent"
                              _hover={{
                                background: 'transparent',
                              }}
                              aria-label="Visualizar solicitação"
                              icon={<Icon as={FaEye} />}
                              onClick={() => goToVisualization(contract.id)}
                            />
                          </Tooltip>
                        )}
                      {(contract.status === 1 ||
                        contract.status === 2 ||
                        contract.status === 3) && ( // Termo de rescisao enviado, aguardando assinatura e termo assinado
                        <Tooltip label="Visualizar solicitação">
                          <IconButton
                            colorScheme="gray"
                            background="transparent"
                            _hover={{
                              background: 'transparent',
                            }}
                            aria-label="Visualizar solicitação"
                            icon={<Icon as={FaEye} />}
                            onClick={() =>
                              goToVisualizationAnalysis(contract.id)
                            }
                          />
                        </Tooltip>
                      )}
                      {contract.status === 1 && isClient && (
                        <Tooltip label="Seguir com rescisão">
                          <IconButton
                            colorScheme="gray"
                            background="transparent"
                            _hover={{
                              background: 'transparent',
                            }}
                            disabled={isFetchingVerifyToken}
                            aria-label="Seguir com rescisão"
                            icon={
                              isFetchingVerifyToken &&
                              selectedTerminationId === contract.id ? (
                                <Spinner />
                              ) : (
                                <Icon as={HiOutlineDocumentCheck} />
                              )
                            }
                            onClick={() => generateToken(contract.id)}
                          />
                        </Tooltip>
                      )}
                      {(contract.status === 1 ||
                        contract.status === 2 ||
                        contract.status === 3) && (
                        <Tooltip
                          label={
                            contract.status === 3
                              ? 'Baixar Termo Assinado'
                              : 'Baixar Termo'
                          }
                        >
                          <IconButton
                            colorScheme="gray"
                            background="transparent"
                            _hover={{
                              background: 'transparent',
                            }}
                            aria-label="Send email"
                            disabled={isFetchingDownloadTermination}
                            icon={
                              isFetchingDownloadTermination ? (
                                <Spinner />
                              ) : (
                                <Icon as={FiDownload} />
                              )
                            }
                            onClick={() => downloadTermination(contract.id)}
                          />
                        </Tooltip>
                      )}
                    </Td>
                    <Td display={{ md: 'none', sm: 'none' }} />
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan={7}>
                    <Flex justify="center">
                      {isLoading ? (
                        <Spinner />
                      ) : (
                        <Text>Nenhuma solicitação encontrada.</Text>
                      )}
                    </Flex>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>

        <Flex justify="flex-end">
          {data && (
            <Pagination
              totalCountOfRegisters={data.count}
              currentPage={Number(parametros.pagina) ?? 1}
              onPageChange={_page => {
                setParametros({ ...parametros, pagina: _page.toString() });
                updateUrlParams(
                  { ...parametros, pagina: _page.toString() },
                  history,
                );
                queryClient.invalidateQueries(
                  'resquest-contract-termination-list',
                );
              }}
              onPageSizeChange={handlePageSizeChange}
              registersPerPage={Number(parametros.paginacao) ?? PAGE_SIZE}
            />
          )}
        </Flex>
      </Stack>
      <ConfirmationDialog
        titulo="Seguir com a rescisão do contrato"
        onClose={onCloseModalToken}
        confirmar={verifyToken}
        isOpen={isOpenModalToken}
        isDisabled={codigo.length < 6 || isFetching || isFetchingVerifyToken}
        btnConfirmText="Aceitar"
        closeOnOverlayClick={false}
        closeOnEsc={false}
      >
        <Text>
          Para confirmar o aceite da proposta, insira o código token enviado
          para o seu e-mail cadastrado{' '}
          <span style={{ color: '#2472a9' }}>{getUserEmailFormatted()}</span>:
        </Text>
        <FormControl>
          <InputGroup justifyContent="start">
            <HStack>
              <PinInput
                size="lg"
                onChange={event => handleCodigoChange(event)}
                isDisabled={isFetching || isFetchingVerifyToken}
              >
                <PinInputField />
                <PinInputField />
                <PinInputField />
                <PinInputField />
                <PinInputField />
                <PinInputField />
              </PinInput>
            </HStack>
          </InputGroup>
          <HStack marginTop={4} justifyContent="space-between">
            <ChakraButton
              color="#5b8af0"
              variant="link"
              onClick={regenerateToken}
              fontSize="sm"
            >
              Reenviar token via e-mail{' '}
            </ChakraButton>
            {(isFetching || isFetchingVerifyToken) && <Spinner />}
          </HStack>
        </FormControl>
      </ConfirmationDialog>
    </Box>
  );
};
